import { useCallback } from 'react'

import { archiverFolder, undefinedEndDate, nonEditableStatus } from '../config';
import { useProfile } from '../api/hooks/profile'

export default function useWhoami(contract) {
    const { profile  } = useProfile()
    const getWhoamiFor = useCallback((contract) => {
        return new Whoami(contract, profile.email, profile._id, profile.roles, profile.organization);
    }, [profile.email, profile._id, profile.roles, profile.organization])

    return { whoami: contract ? getWhoamiFor(contract) : undefined, getWhoamiFor };  
}

class Whoami {
  constructor(contract, email, userId, roles, organization, profileGroups) {
    this.profileGroups = profileGroups ?? [];
    this.email = email;
    this.userId = userId;
    this.roles = roles ?? [];
    this.organization = organization;
    this.owner = contract.user?._id ?? contract.user;
    this.status = contract.status;
    this.partnerId = contract.partnerId;
    this.contractId = contract._id;
    this.importedPdf = contract.importedPdf;
    this.workflow = contract.workflow;
    this.workflowStopped = contract.workflowStopped;
    this.workflowStep = contract.workflowStep;
    this.wordonline = contract.wordonline;
    this.watchers = contract.watchers ?? [];
    this.vobo = contract.vobo ?? [];
    this.locked = contract.locked;
    this.participants = contract.participants ?? [];
    this.uploadMethod = contract.uploadMethod;
    this.signatureType = contract.signatureType;
    this.prefilled = contract.prefilled;
    this.parentReference = contract.parentReference;
    this.reference = contract.reference;
    this.type = contract.type;
    this.password = contract.password;
    this.archived = contract.archived;
    this.clientShared = contract.clientShared;
    this.originalOwner = contract.originalOwner;
    this.issTemplate = contract.isTemplate;
    this.record = contract.record;
    this.nom151 = contract.nom151;
    this.groups = contract.groups ?? [];
    this.signInOrder = contract.signInOrder;
    this.isApplication = contract.isApplication;
    this.questions = contract.questions ?? [];
    this.formUrl = contract.formUrl;
    this.workflowStepPopulated = contract.workflowStepPopulated;
    this.workflowNextStepPopulated = contract.workflowNextStepPopulated;
    this.delegation = contract.delegation ?? [];
    this.votes = contract.votes ?? [];
    this.voters = contract.voters ?? [];
    this.voteStarted = contract.voteStarted;
    this.votingEndDate = contract.votingEndDate;
    this.canChangeVote = contract.canChangeVote; // this.assembly();
    this.metadata = contract.metadata;
    this.childOf = contract.childOf;
    this.formUrl = contract.formUrl;
    this.forms = contract.forms ?? [];
    this.revision = contract.revision ?? [];
  }
  set(name, value) {
    this[name] = value;
  }

  isForm() {
    return !!(this.formUrl)
  }

  canForwardWorkflow() {

    const currentStepType = this.workflowStepPopulated?.type;
    const currentStepUserId = this.workflowStepPopulated?.user?._id;

    const nextStepType = this.workflowNextStepPopulated?.type;

    if (!nextStepType || !currentStepType) return false;

    return (currentStepType === 'delegate' && currentStepUserId === this.userId) ||
      currentStepType === 'group-delegate';
  }

  hasMetadata() {
    return !!(this.metadata)
  }

  isSentToDownload() {
    return this.status === 'SENT_TO_DOWNLOAD';
  }

  isBeingNegotiated() {
    return this.status === 'NEGOTIATION'
  }

  contractIsApplication() {
    return !!(this.isApplication);
  }

  isParticipant() {
    return this.participants.some(part => part.email === this.email);
  }

  isPartnerClient() {
    return this.partnerId === this.organization;
  }

  hasNom151() {
    return !!this.nom151
  }

  isExpired() {
    return this.status === 'EXPIRED'
  }

  hasToSign() {
    return this.status === 'SENT_TO_SIGN' &&
      this.participants.some(participant => this.email === participant.email && participant.status === 'RECEIVED_TO_SIGN');
  }

  hasToComplete() {
    return this.status === 'SENT_TO_FILL' &&
      this.participants.some(participant => this.email === participant.email && participant.status === 'RECEIVED_TO_FILL');
  }

  hasToValidate() {
    return this.signInOrder === true &&
      this.participants.some(participant =>
        participant.signOrder >= 0 &&
        participant.isCurrentSigner &&
        participant.requireUserValidation?._id === this.userId
      );
  }

  hasShield3() {
    return this.participants.some(participant =>
        participant.requestAgreement
      );
  }

  hasShield3Video() {
    return this.participants.some(participant =>
        participant.requestAgreement 
        && !!participant.video
      );
  }

  myGroupHasToValidate() {
    return !!(this.profileGroups?.length &&
      this.signInOrder === true &&
      this.participants.some(participant =>
        participant.signOrder >= 0 &&
        participant.isCurrentSigner &&
        this.profileGroups.map(p => p._id).includes(participant.requireGroupValidation?._id)
      ));
  }

  isTemplate() {
    return this.issTemplate;
  }

  hasGroups() {
    return !!(this.groups);
  }

  hasQuestionare() {
    return !!this.questions?.length;
  }

  getUserForm() {
    return this.forms.find(f => f.user === this.userId)
  }

  hasForm() {
    return !!this.getUserForm();
  }

  participantsCountEqualTo(count) {
    return this.participants.length === count;
  }

  participantsCountEqualOrLessThan(count) {
    return this.participants.length <= count;
  }

  roleCan(role) {
    return this.roles.includes(role);
  }

  isLocked() {
    return this.locked;
  }

  isContentLocked() {
    return this.isEditorContract() && this.isLocked();
  }

  isEditable() {
    return !nonEditableStatus.includes(this.status) &&
      !this.isSigned() &&
      !this.isFinalized() &&
      !this.someoneSigned();
  }

  someoneSigned() {
    return this.participants.some(p => p.status === 'SENT_SIGNED');
  }

  isSentToSign() {
    return this.status === 'SENT_TO_SIGN';
  }

  isSentToComplete() {
    return this.status === 'SENT_TO_FILL';
  }

  isWatcher() {
    return !!this.watchers.find(w => (w?._id === this.userId || w === this.userId));
  }

  everyoneApproved() {
    return this.hasVobo() && this.vobo.every(v => v.status === 'approved');
  }

  someoneApproved() {
    return this.hasVobo() && this.vobo.some(v => v.status === 'approved');
  }

  someoneGaveVobo() {
    return this.hasVobo() && this.vobo.some(v => v.status !== 'sent');
  }

  hasVobo() {
    return !!this.vobo.length;
  }

  getMyVoBo() {
    return this.vobo.find(vbo => vbo.user?._id === this.userId);
  }

  currentUserHasGiveVobo() {
    const vobo = this.getMyVoBo();
    return !!(vobo && ['approved', 'rejected'].includes(vobo.status));
  }

  currentUserHasToVobo() {
    return !!(this.getMyVoBo()?.status === 'sent')
  }

  isOwner() {
    return this.owner === this.userId;
  }

  isPdf() {
    return !!this.importedPdf;
  }

  isCollaborator() { }

  isEditorContract() {
    return this.editor === 'contract';
  }

  isWordOnline() {
    return !!Object.keys(this.wordonline || {}).length;
  }

  hasWorkflow() {
    return !!this.workflow;
  }

  hasWorkflowStopped() {
    return !!(this.workflow && this.workflowStopped);
  }

  hasWorkflowInitiated() {
    return !!this.workflowStep;
  }

  workflowIsInitiated() {
    return !!(this.hasWorkflow() && this.hasWorkflowInitiated());
  }

  isSigningOrCompleting() {
    return ['SENT_TO_FILL', 'SENT_TO_SIGN'].includes(this.status);
  }

  isSigned() {
    return ['RECEIVED_SIGNED', 'RECEIVED_TO_DOWNLOAD', 'SENT_TO_DOWNLOAD'].includes(this.status);
  }

  canBeFinalized() {
    return !!(this.isSigned() && this.isOwner() && !this.isFinalized());
  }

  isFinalized() {
    return this.status === 'FINALIZED';
  }

  canBeSigned() {
    return ['', 'APPROVED', 'PRE_APPROVED'].includes(this.status);
  }

  isUploadMethodMultiple() {
    return this.uploadMethod === 'multiple';
  }

  isFadSignature() {
    return this.signatureType === 'fad';
  }

  isAutographSignature() {
    return this.signatureType === 'autograph';
  }

  isCertificateSignature() {
    return this.signatureType === 'certificate';
  }

  hasNoSignature() {
    return this.signatureType === 'none';
  }

  isPrefilled() {
    return !!this.prefilled;
  }

  isCanceled() {
    return this.status === 'CANCELED';
  }

  isChildContract() {
    return !!this.parentReference;
  }

  isParentContract() {
    return !!this.reference?.length
  }

  isFolder() {
    return this.type === 'contracts' || this.type === 'templates';
  }

  isDocOrDocx() {
    return this.isWordOnline() && this.wordonline?.key?.includes('.doc');
  }

  isXlsOrXlsX() {
    return this.isWordOnline() && this.wordonline?.key?.includes('.xlx');
  }

  isPasswordProtected() {
    return !!this.password;
  }

  isDraft() {
    return this.status === '';
  }

  isInArchiver() {
    return this.childOf?._id === archiverFolder;
  }

  isArchived() {
    return this.childOf?._id === archiverFolder;
  }

  isApproved() {
    return ['APPROVED', 'ACCEPTED_NEGOTIATION'].includes(this.status);
  }

  isTemplateShareToClient() {
    return !!this.clientShared;
  }

  isTemplateTaken() {
    return !!(this.isTemplate() && this.originalOwner);
  }

  isInsideRecord() {
    return !!this.record;
  }

  isInNegotiation() {
    return this.status === 'NEGOTIATION';
  }

  isPreApproved() {
    return this.status === 'PRE_APPROVED';
  }

  getParticipantsNames() {
    return this.participants.map(part => ({
      name: `${part.name || ''}${part.last_name ? ` ${part.last_name}` : ''}`,
      long: `${part.name || ''}${part.last_name ? ` ${part.last_name}` : ''}`.length > 10
    }));
  }

  getCurrentDelegation() {
    return this.delegation.find(d => d.current);
  }

  getSentVobo() {
    return this.vobo.filter(v => v.status === 'sent');
  }

  hasToVote() {
    if (!this.voteStarted) return false;
    if (this.voteHasEnded()) return false;
    if (this.canChangeVote) return true;
    return this.votes.find(v => v.status === 'blank' && v.user === this.userId)

  }

  voteHasEnded() {
    if (!this.voteStarted) return false;
    if (new Date(this.votingEndDate) < new Date()) return true;
    if (this.votingEndDate === undefinedEndDate && !this.votes?.some(v => v.status === 'blank')) return true;
    return false;
  }

  getVoteStatus() {
    if (!this.voteStarted) return null;
    if (new Date(this.votingEndDate) < new Date()) return 'ended';
    if (this.votingEndDate === undefinedEndDate && !this.votes?.some(v => v.status === 'blank')) return 'ended';
    return 'ongoing';
  }

  myGroupCanTakeVobo() {
    const myGroups = this.profileGroups?.map?.(o => o['_id']);
    if (!myGroups) return false;
    return this.vobo.some(v => myGroups.includes(v.userGroup?._id));
  }

  myGroupCanGrab() {
    const myGroups = this.profileGroups?.map?.(o => o._id);
    if (!myGroups) return false;
    return this.groups.some(group => group.canOwn && myGroups.includes(group.group?._id ?? group.group));
  }

  canApproveNegotiation() {
    return !!(this.status === 'NEGOTIATION' &&
      this.participants.length &&
      this.participants.filter(p => !p.isNotNegotiator).every(p => p.status === 'ACCEPTED_NEGOTIATION') &&
      this.isOwner() && this.isWordOnline());
  }


  meAsParticipant() {
    if (!this.participants) return null;
    return this.participants.find(p => p.email === this.email);
  }
  isNegotiator() {
    if (!this.participants) return null;
    return this.participants.find(p => p.email === this.email && !p.isNotNegotiator);
  }
  iHaveNegotiationTurn() {
    const meParticipant = this.meAsParticipant();
    return Boolean(this.isInNegotiation() && meParticipant?.status === 'NEGOTIATION_TAKEN');
  }
  someoneHasNegotiationTurn() {
    if (!this.participants) return null;
    return Boolean(this.participants.some((p) => p.status === 'NEGOTIATION_TAKEN'));
  }
  getRevisionCollaborator() {
    return this.revision.reduce((collab, item) => {
      if (item.user && item.user._id === this.userId && item.status === 'sent') return item
      return collab
    }, {})
  }

}

